import { createSharedComposable } from '@vueuse/core';
import { computed, ref } from 'vue';
import type {
    AgentConfirmMessageData,
    AgentMessageData,
    AgentTitleMessageData,
    ConfirmMessageData,
    EventData,
    InterruptedMessageData,
    ModuleContentMessageData,
    PatentRecallEvaluationMessageData,
    ThinkMessageData,
    TitleMessageData,
    UserConfirmMessageData,
    UserMessageData,
    WelcomeMessageData,
} from '@/domains/chat/types/triz-events'
import { SSE_EVENT_TYPE } from '@/domains/chat/types/triz-events'
import { useScrollToBottom } from '../use-scroll-to-bottom'
import {
    type AgentComponent,
    COMPONENT_TYPE,
    type MergedBotMessageData,
    type MessageComponent,
    type UserComponent,
} from '@/domains/chat/types/triz-component'
import { TrizService } from '@/domains/triz/services/triz-service';
import { injectService } from '@patsnap/tahiti-core';
import { useRoute, useRouter } from 'vue-router';
import { MODULE_CONTENT_KEY } from '@/domains/chat/types';
import { useTrizState } from './use-triz-state';
import { ALL_MODULE_KEYS } from '@/domains/triz/constants/module-keys';

export const useMessages = createSharedComposable(() => {
    const trizService = injectService<TrizService>(TrizService);
    const messages = ref<MessageComponent[]>([]);
    const mergedBotMessages = ref<Map<string, MergedBotMessageData>>(new Map());
    const route = useRoute();
    const router = useRouter();

    const { trizTitle, openDetail, userClosedDetail, updateOpenDetail, updateTrizTitle } = useTrizState();

    // 检查是否是回放模式
    const isReplyMode = ref(false);

    // 已处理的消息ID集合，用于去重（避免重连时重复处理相同消息）
    const processedMessageIds = ref<Set<number>>(new Set());

    // 用户手动导航标记：当用户手动切换路由时设置为true，阻止自动跳转
    const userManuallyNavigated = ref(false);

    // 任务状态管理
    const taskStarted = ref(false);
    const taskEnded = ref(true);

    // 历史数据完成检测定时器
    let historyCompleteTimer: NodeJS.Timeout | null = null;

    // 计算属性：是否正在生成中（基于 TASK_START 和 TASK_END 事件）
    const isTaskRunning = computed(() => {
        return taskStarted.value && !taskEnded.value;
    });

    const getVersion = computed(() => {
        const versionNumber = Number(route.query.version)
        const versionList = messages.value.filter(item => item.type === COMPONENT_TYPE.AGENT && (item.content as MergedBotMessageData).step === 3)
        const index = versionList.findIndex(item => {
            const _row = (item.content as MergedBotMessageData).module_list?.find((it) => it?.module_key === MODULE_CONTENT_KEY.BATCH_SOLUTION_IDEAS)
            return _row?.version_number === versionNumber
        })
        return `${(index <= 0 ? 1 : index + 1)}`
    });

    const { scrollToBottom, resetAutoScroll } = useScrollToBottom('.agent-panel-scroll-container');

    function initReplyMode() {
        isReplyMode.value = !!route.query.idea_id || !!route.query.version
    }

    function resetReplyMode() {
        isReplyMode.value = false;
    }
    // 导航到目标步骤和版本
    function navigateToTargetStep() {
        const { version, idea_id, module_key } = route.query;
        router.replace({
            query: {
                idea_id,
                version,
                module_key,
            },
        });
    }
    /**
     * 标记用户手动导航
     * 当用户手动切换路由时调用此方法，阻止后续的自动跳转
     */
    function markUserNavigation() {
        userManuallyNavigated.value = true;
    }

    // 启动历史数据完成检测
    function startHistoryCompleteCheck() {
        // 清除之前的定时器
        if (historyCompleteTimer) {
            clearTimeout(historyCompleteTimer);
        }

        // 1秒后检查是否还有新数据
        historyCompleteTimer = setTimeout(() => {
            resetReplyMode()
            navigateToTargetStep();
        }, 1000);
    }

    function reset() {
        // 保留全局唯一的 Welcome 消息
        const welcomeMessage = messages.value.find(m => m.type === COMPONENT_TYPE.WELCOME);

        // 重置消息列表，保留 Welcome 消息
        messages.value = welcomeMessage ? [welcomeMessage] : [];
        mergedBotMessages.value.clear();
        processedMessageIds.value.clear();
        updateOpenDetail(false)
        updateTrizTitle('');
        resetAutoScroll(true);
        // 重置任务状态
        taskStarted.value = false;
        taskEnded.value = true;
        // 清理历史数据完成检测定时器
        if (historyCompleteTimer) {
            clearTimeout(historyCompleteTimer);
            historyCompleteTimer = null;
        }
    }

    /**
     * 处理 MODULE_CONTENT 消息
     */
    function handleModuleContent(message: ModuleContentMessageData, isRouterReplace = true) {
        const { data } = message;
        const { module_key, version_number } = data;
        if (module_key === MODULE_CONTENT_KEY.BATCH_PATENT_RECALL) {
            trizService.mergePatentRecallData(message as unknown as PatentRecallEvaluationMessageData);
        } else if (module_key === MODULE_CONTENT_KEY.BATCH_SOLUTION_IDEAS) {
            trizService.mergeSolutionIdeas(message);
        } else {
            trizService.setModuleData(version_number, module_key, data);
        }
        // 在回放模式下不修改路由参数的
        // 如果用户手动导航过，也不自动跳转
        if (!isReplyMode.value && isRouterReplace && !userManuallyNavigated.value) {
            router.replace({
                query: {
                    version: data.version_number,
                    module_key: data.module_key,
                },
            });
        }
        if (!userClosedDetail.value && [MODULE_CONTENT_KEY.BATCH_PATENT_RECALL,
            MODULE_CONTENT_KEY.BATCH_SOLUTION_IDEAS].includes(module_key)) {
            updateOpenDetail(true);
        }
    }

    /**
     * 合并或创建Bot消息
     * @param message
     * @returns 合并后的Bot消息
     */
    function mergeOrCreateBotMessage(message: AgentTitleMessageData | AgentMessageData | ConfirmMessageData | ThinkMessageData | ModuleContentMessageData | AgentConfirmMessageData): MergedBotMessageData {
        // 如果没有 message_group，生成一个唯一的 ID（基于消息 id 和时间戳）
        const messageGroup = (message.data as { message_group?: string }).message_group || `agent_${message.id}_${Date.now()}`;
        const existing = mergedBotMessages.value.get(messageGroup);

        const merged: MergedBotMessageData = existing || {
            message_group: messageGroup,
            business_id: message.business_id,
        };

        // 根据消息类型更新合并数据
        // @ts-ignore
        // @ts-ignore
        switch (message.data.message_type) {
        case SSE_EVENT_TYPE.AGENT_TITLE:
            merged.title = (message as AgentTitleMessageData).data.message;
            break;

        case SSE_EVENT_TYPE.AGENT:
            merged.agent_message = (message as AgentMessageData).data.message;
            break;

        case SSE_EVENT_TYPE.CONFIRM:
            /*
            * 当前判断节点完成（message节点无可用信息判断节点，出此下策）
            *
            * */
            if (merged.module_list?.length) {
                if (!merged.isCompleted) {
                    merged.isCompleted = true;
                }
            }
            merged.confirm_data = (message as ConfirmMessageData);
            if (merged.step === 2) {
                setTimeout(() => {
                    updateOpenDetail(true);
                }, 1100)
            }
            break;

        case SSE_EVENT_TYPE.AGENT_CONFIRM:
            merged.step = message.data.step;
            merged.agent_confirm_data = (message as AgentConfirmMessageData);
            break;

        case SSE_EVENT_TYPE.MODULE_CONTENT_THINK:
            if (message.data.stream_message) {
                const thinkingMsg = message as ThinkMessageData;
                merged.step = thinkingMsg.data?.step || undefined;
                // 流式场景：拼接之前的全量文本 + 新的增量文本
                const existingMessage = existing?.think_message?.data.message || '';
                const newStreamMessage = thinkingMsg.data.stream_message || '';
                merged.think_message = {
                    ...message,
                    data: {
                        ...message.data,
                        // 拼接文本：旧的全量 + 新的增量
                        message: existingMessage + newStreamMessage,
                    },
                };
            } else if (message.data.message) {
                // 回放场景或全量消息：如果有已有内容则拼接，否则直接设置
                if (existing?.think_message) {
                    const existingMessage = existing.think_message.data.message || '';
                    const newMessage = message.data.message || '';
                    merged.think_message = {
                        ...message,
                        data: {
                            ...message.data,
                            // 拼接文本：旧的消息 + 新的全量消息
                            message: existingMessage.indexOf(newMessage) >= 0 ? existingMessage.replace(newMessage, newMessage) : existingMessage + newMessage,
                        },
                    };
                } else {
                    // 第一次收到thinking消息
                    merged.think_message = (message as ThinkMessageData);
                }
            }
            break;

        case SSE_EVENT_TYPE.MODULE_CONTENT: {
            const moduleContentMsg = message as ModuleContentMessageData;
            handleModuleContent(moduleContentMsg);

            merged.step = moduleContentMsg.data.step;

            // 初始化 module_list 为数组（如果不存在）
            if (!merged.module_list) {
                merged.module_list = [];
            }

            // 初始化 merged 的 isCompleted 为 false（如果不存在）
            if (merged.isCompleted === undefined) {
                merged.isCompleted = false;
            }
            const moduleData = moduleContentMsg.data;
            const moduleKey = moduleData.module_key;

            // 查找是否已存在相同 module_key 的项
            const existingIndex = merged.module_list.findIndex(item => item.module_key === moduleKey);

            if (existingIndex >= 0) {
                // 如果存在，覆盖该项
                merged.module_list[existingIndex] = {
                    ...moduleData,
                    isCompleted: false,
                };
            } else {
                // 如果不存在，push 新项
                merged.module_list.push({
                    ...moduleData,
                    isCompleted: false,
                });
            }
            break;
        }
        }

        mergedBotMessages.value.set(messageGroup, merged);
        return merged;
    }

    /**
     * 设置节点完成状态
     * @param data NODE_END 事件的数据
     */
    function setNodeComplete(data: any) {
        // 在 messages 中查找 message_group 相等的项
        const targetMessage = messages.value.find(item => {
            return item.type === COMPONENT_TYPE.AGENT &&
                   item.content.message_group === data.message_group;
        });

        if (!targetMessage) {
            console.warn('[setNodeComplete] 未找到匹配的消息:', data.message_group);
            return;
        }

        // 在该项的 module_list 中查找 module_key 与 data.message 相等的项
        const targetModule = targetMessage.content.module_list?.find(module => {
            return module.module_key === data.message;
        });

        /*
        * 特殊处理
        * BATCH_PATENT_RECALL 完成后节点继续保持loading状态
        * 等待PATENT_TRIZ_FEATURES 完成后再设置PATENT_RECALL结束loading
        * */
        if (targetModule?.module_key === MODULE_CONTENT_KEY.BATCH_PATENT_RECALL) return;
        else if (data.message === MODULE_CONTENT_KEY.BATCH_PATENT_TRIZ_FEATURES) {
            const beforeModule = targetMessage.content.module_list?.find(module => {
                return module.module_key === MODULE_CONTENT_KEY.BATCH_PATENT_RECALL;
            });
            if (beforeModule) beforeModule.isCompleted = true;
        }

        if (!targetModule) {
            console.warn('[setNodeComplete] 未找到匹配的模块:', data.message);
            return;
        }

        // 设置该项的 isCompleted: true
        targetModule.isCompleted = true;
    }

    /**
     * 处理消息
     */
    function onMessage(message: EventData) {
        // 检查消息是否已处理过（根据消息 id 去重，避免重连时重复处理）
        if ('id' in message && message.id && processedMessageIds.value.has(message.id)) {
            return; // 已处理过的消息直接跳过
        }

        if ('id' in message && message.id) {
            processedMessageIds.value.add(message.id);
        }

        // 处理 TASK_START 和 TASK_END 事件
        if (message.data.message_type === SSE_EVENT_TYPE.TASK_START) {
            taskStarted.value = true;
            taskEnded.value = false;
            return;
        } else if (message.data.message_type === SSE_EVENT_TYPE.TASK_END) {
            taskEnded.value = true;
            // 当前只设置了isCompleted 是否完成
            messages.value.forEach(item => {
                if (item.type === COMPONENT_TYPE.AGENT) {
                    if (item.content?.module_list?.length) {
                        item.content.isCompleted = true;
                    }
                }
            })

            // 如果是回放模式，启动历史数据完成检测
            if (isReplyMode.value) {
                startHistoryCompleteCheck();
            }
            return;
        }

        if (message.data.message_type === SSE_EVENT_TYPE.TITLE) {
            updateTrizTitle((message as TitleMessageData).data.message);
        } else if (message.data.message_type === SSE_EVENT_TYPE.USER || message.data.message_type === SSE_EVENT_TYPE.USER_CONFIRM) {
            // 检查消息列表中是否已有此message_group，如果有则更新，否则添加
            const existingMessageIndex = messages.value.findIndex(
                m => m.type === COMPONENT_TYPE.USER && message.data.message_group && (m.content as MergedBotMessageData)?.data?.message_group === message.data.message_group
            );
            if (existingMessageIndex >= 0) {
                // 更新现有消息
                (messages.value[existingMessageIndex] as UserComponent).content = message as UserMessageData | UserConfirmMessageData;
            } else {
                // 添加新消息
                messages.value.push({
                    type: COMPONENT_TYPE.USER,
                    content: message as UserMessageData | UserConfirmMessageData,
                });
            }
            scrollToBottom(true);
        } else if ([SSE_EVENT_TYPE.AGENT_TITLE, SSE_EVENT_TYPE.AGENT, SSE_EVENT_TYPE.CONFIRM, SSE_EVENT_TYPE.MODULE_CONTENT_THINK, SSE_EVENT_TYPE.MODULE_CONTENT, SSE_EVENT_TYPE.AGENT_CONFIRM].includes(message.data.message_type)) {
            /*
            * MODULECONTENT中存在无需展示的模块,例如详情：图片、视频等
            * 下面方法拦截，存入临时变量中，不展示
            * */

            if ([SSE_EVENT_TYPE.MODULE_CONTENT].includes(message.data.message_type)) {
                if (!ALL_MODULE_KEYS[(message as ModuleContentMessageData).data.module_key]) {
                    handleModuleContent(message as ModuleContentMessageData, false);
                    return;
                }
            }
            // if (!ALL_MODULE_KEYS[(message as ModuleContentMessageData).data.module_key]) return;

            const mergedData = mergeOrCreateBotMessage(message as AgentTitleMessageData | AgentMessageData | ConfirmMessageData | ThinkMessageData | ModuleContentMessageData);

            // 检查消息列表中是否已有此message_group，如果有则更新，否则添加
            const existingMessageIndex = messages.value.findIndex(
                m => m.type === COMPONENT_TYPE.AGENT && (m.content as MergedBotMessageData).message_group === mergedData.message_group
            );

            if (existingMessageIndex >= 0) {
                // 更新现有消息
                (messages.value[existingMessageIndex] as AgentComponent).content = mergedData;
            } else {
                // 添加新消息
                messages.value.push({
                    type: COMPONENT_TYPE.AGENT,
                    content: mergedData,
                });
            }
            if ([SSE_EVENT_TYPE.CONFIRM].includes(message.data.message_type)) {
                scrollToBottom(true);
            } else {
                scrollToBottom();
            }
        } else if (message.data.message_type === SSE_EVENT_TYPE.WELCOME) {
            const hasWelcome = messages.value.some(m => m.type === COMPONENT_TYPE.WELCOME);
            if (!hasWelcome) {
                messages.value.push({
                    type: COMPONENT_TYPE.WELCOME,
                    content: message as WelcomeMessageData,
                });
            }
            scrollToBottom(true);
        } else if (message.data.message_type === SSE_EVENT_TYPE.NODE_START) {
            const { data } = message;
            /*
            * 单独处理。PATENT_RECALL前置过久，提前跳转到对应页面加载
            * */
            if (data.message === MODULE_CONTENT_KEY.BATCH_PATENT_RECALL) {
                messages.value.push({
                    type: COMPONENT_TYPE.AGENT,
                    content: {
                        step: data.step,
                        message_group: data.message_group,
                        module_list: [
                            {
                                module_key: MODULE_CONTENT_KEY.BATCH_PATENT_RECALL,
                                message: data.message,
                                message_group: data.message_group,
                                step: data.step,
                                version_number: data.version_number,
                            }
                        ],
                    },
                });
                // 必须push一条数据，不然页面会走入 “暂无数据”
                trizService.setModuleData(data.version_number, data.message, {
                    ...data,
                    module_key: data.message,
                });

                if (!isReplyMode.value && !userManuallyNavigated.value) {
                    router.replace({
                        query: {
                            version: data.version_number,
                            module_key: data.message,
                        },
                    });
                }
            }
        } else if (message.data.message_type === SSE_EVENT_TYPE.NODE_END) {
            const { data } = message;
            // 如果当前路由的信息和 结束节点信息相等，那么设置markUserNavigation
            if (Number(route.query.version) === data.version_number && route.query.module_key === data.message) {
                userManuallyNavigated.value = false
            }
            trizService.updateStepModuleStatus(data.version_number, data.message, true)
            setNodeComplete(data);
        } else if (message.data.message_type === SSE_EVENT_TYPE.RAG_RECALL_PROCESS) {
            // 通过groupid查询 PATENT_RECALL来设置ifEnd
            if (!message.data?.message?.rag_recall_finish) return;
            let patentRecallInfo: any;
            messages.value.forEach((item) => {
                if (item.type === COMPONENT_TYPE.AGENT && item.content.message_group === message.data.message_group && item.content?.module_list) {
                    patentRecallInfo = item.content.module_list.find((module) => {
                        return module.module_key === MODULE_CONTENT_KEY.BATCH_PATENT_RECALL;
                    });
                }
            })

            trizService.updateStepModuleStatus(patentRecallInfo.version_number, MODULE_CONTENT_KEY.BATCH_PATENT_RECALL, true)
        } else if (message.data.message_type === SSE_EVENT_TYPE.INTERRUPTED) {
            messages.value.forEach(item => {
                if (item.type === COMPONENT_TYPE.AGENT) {
                    if (item.content?.module_list?.length) {
                        item.content.module_list.forEach(item => {
                            if (!item.isCompleted) item.isCompleted = true;
                        })
                    }
                }
            })
            messages.value.push({
                type: COMPONENT_TYPE.INTERRUPTED,
                content: message as InterruptedMessageData,
            });
            scrollToBottom(true);
        }
    }

    return {
        initReplyMode,
        reset,
        messages,
        onMessage,
        scrollToBottom,
        openDetail,
        trizTitle,
        markUserNavigation, // 暴露给外部使用
        // 导出任务状态
        taskStarted,
        taskEnded,
        isTaskRunning,
        getVersion,
    }
})
